/**
 * The sheet's one menu shape: a single-choice picker whose ineligible entries stay listed and say
 * why they are ineligible.
 *
 * Three controls on this surface are that object — the family („Typ progresji"), the column the
 * client climbs („Podopieczny progresuje w") and a row's set role — and they were three copies of
 * the same twenty-five lines of Base UI scaffolding, down to the `closeOnClick` comment. A fix to
 * one of them reached one of them, which is how the popup that would not close was fixed twice.
 *
 * ── Every refusal is STATED, never hidden ───────────────────────────────────────────────────
 * An entry the exercise cannot take is a DISABLED entry carrying its reason, not an absent one: a
 * menu two entries long on one exercise and four on the next, with nothing saying why, is the shape
 * this component exists to prevent. It is also the only arrangement that puts the reason within
 * reach of the keyboard.
 *
 * ── What each caller still owns ─────────────────────────────────────────────────────────────
 * Its TRIGGER — a chip, a rowheader, whatever the mock draws there — and the CONTENT of each entry,
 * which differs by more than words (a badge, a second note, a price). Those are the two things the
 * three genuinely disagree about; everything between them is here.
 *
 * ── An ACT is not a choice ──────────────────────────────────────────────────────────────────
 * `action` is a plain item under a separator, after the radios in arrow order: something the menu
 * DOES rather than a value the control can hold, so it is never drawn as one that could be checked.
 * The family menu's „Resetuj progresję" is the one today (owner, 15 September 2026).
 */

import { Menu } from '@base-ui/react/menu';
import type { ComponentProps, ReactNode } from 'react';

import menuStyles from '~/shared/ui/MenuSurface.module.css';

/** One entry: its wire value, what it draws, and why it may not be chosen. */
export interface RefusableOption<T extends string> {
  readonly value: T;
  /** Rendered inside the item. A caller's own two-line entry, badge and all. */
  readonly content: ReactNode;
  /** The reason this entry is refused, or null when it may be chosen. */
  readonly isRefused: boolean;
}

interface RefusableRadioMenuProps<T extends string> {
  /**
   * Everything the trigger is, `children` included. Handed over whole rather than assembled here:
   * one caller's trigger is a chip and another's is the matrix's row header, which carries a role, a
   * roving tab index and a `data-cell` address the grid moves focus by — hence the `data-*` opening,
   * which Base UI's own prop type does not admit.
   */
  readonly triggerProps: ComponentProps<typeof Menu.Trigger> &
    Readonly<Record<`data-${string}`, string>>;
  /** The popup's own heading. Omitted where the trigger already names the choice. */
  readonly label?: string;
  readonly value: string;
  readonly options: readonly RefusableOption<T>[];
  readonly onPick: (value: T) => void;
  /** An act at the foot of the menu, or none. It closes the menu like any item. */
  readonly action?: {
    readonly label: string;
    readonly className: string;
    readonly onAction: () => void;
  };
}

export function RefusableRadioMenu<T extends string>({
  triggerProps,
  label,
  value,
  options,
  onPick,
  action,
}: RefusableRadioMenuProps<T>) {
  return (
    <Menu.Root>
      <Menu.Trigger {...triggerProps} />
      <Menu.Portal>
        <Menu.Positioner className={menuStyles.positioner} sideOffset={6} align="start">
          <Menu.Popup className={menuStyles.popup}>
            {/* The label sits INSIDE the group, and that is not a layout preference: Base UI reads
              * its group off context and THROWS — „MenuGroupContext is missing" — when a
              * `Menu.GroupLabel` stands beside one. Both menus that name their popup (the gate and
              * the row's set role) took the whole builder down on every open. */}
            <Menu.RadioGroup value={value}>
              {label !== undefined && (
                <Menu.GroupLabel className={menuStyles.groupLabel}>{label}</Menu.GroupLabel>
              )}
              {options.map((option) => (
                <Menu.RadioItem
                  key={option.value}
                  className={menuStyles.item}
                  value={option.value}
                  /* Base UI defaults `closeOnClick` to FALSE on a radio item, because a radio GROUP
                    * is often a multi-step filter. These are single-choice pickers — choosing is
                    * finishing — and without this the popup stayed open over the sheet and swallowed
                    * the next press. Verified against the installed `MenuRadioItem.d.ts`, and found
                    * by driving the app. */
                  closeOnClick
                  disabled={option.isRefused}
                  onClick={() => {
                    if (!option.isRefused) onPick(option.value);
                  }}
                >
                  {option.content}
                </Menu.RadioItem>
              ))}
            </Menu.RadioGroup>
            {action !== undefined && (
              <>
                <Menu.Separator className={menuStyles.separator} />
                <Menu.Item className={action.className} onClick={action.onAction}>
                  {action.label}
                </Menu.Item>
              </>
            )}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}
